/**
 * Interactive Anchor Components
 * 
 * React components that render when anchors are parsed from LLM responses.
 */

import React from "react";
import MarketHeatmapCard, { type MapFilter } from "./MarketHeatmapCard";
import { SectorNewsPanel } from "../../../pages/KQuantSmartInsightsPage/HotTopicsSection.SectorNewsPanel";
import type { AnchorParams } from "../utils/anchorParser";
import type { SectorPanelData } from "../../../pages/KQuantSmartInsightsPage/HotTopicsSection.types";

type AnchorComponentProps = {
  componentName: string;
  params: AnchorParams;
  theme?: "light" | "dark";
};

/**
 * Heatmap Anchor Component
 * Supports: [heatmap()], [heatmap(sector="S&P")], [heatmap(collection="sp500", filter="tech")]
 */
function HeatmapAnchor({ params, theme = "dark" }: { params: AnchorParams; theme?: "light" | "dark" }) {
  // Extract parameters with defaults
  const collection = params.collection || undefined;
  const filter = (params.filter || params.sector || "S&P 500") as MapFilter;

  return (
    <div className="my-4">
      <MarketHeatmapCard
        mapFilter={filter}
        theme={theme}
        autoFetch={true}
        initialCollectionId={collection}
      />
    </div>
  );
}

/**
 * Sector News Anchor Component
 * Supports: [sector_news(sector="technology")]
 */
function SectorNewsAnchor({ params, theme = "dark" }: { params: AnchorParams; theme?: "light" | "dark" }) {
  const sector = params.sector || "technology";

  // Mock panel data - in production, this would fetch real data
  const mockPanel: SectorPanelData = {
    sectorId: sector,
    label: sector.charAt(0).toUpperCase() + sector.slice(1),
    count: 5,
    bearish: [],
    bullish: [],
    news: [],
  };

  return (
    <div className="my-4">
      <SectorNewsPanel panel={mockPanel} theme={theme} loading={false} />
    </div>
  );
}

/**
 * Main Anchor Component Router
 * Routes to the appropriate component based on componentName
 */
export function AnchorComponent({ componentName, params, theme = "dark" }: AnchorComponentProps) {
  switch (componentName) {
    case "heatmap":
      return <HeatmapAnchor params={params} theme={theme} />;
    
    case "sector_news":
      return <SectorNewsAnchor params={params} theme={theme} />;
    
    default:
      // Unknown component - render as text
      return (
        <span className="text-red-500 text-sm">
          [Unknown component: {componentName}]
        </span>
      );
  }
}
